import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ROUTES } from "../lib/routes";
import { classMode } from "../lib/classMode";
import { isNight, loadRestClock, loadRestPrefs, restDue, restTick, saveRestClock, type RestClock } from "../lib/rest";
import { AdultGate } from "./AdultGate";

/** 쉬어 가기를 안 띄우는 곳 — 어른이 보는 화면 */
const ADULT_PATHS = [ROUTES.family, ROUTES.privacy, ROUTES.licenses, ROUTES.guide, ROUTES.familyBook];
const TICK_MS = 15_000;

/**
 * ⏰ **쉬어 가기** — 보호자가 고른 시간이 되면 「병아리가 쉬자고 해요」가 덮는다 (2026-10-24 · 판정은 lib/rest).
 * 넘기려면 어른 확인(셈) — 아이가 혼자 끄면 장식이다. 수업 중(선생님이 정한다)·어른 화면에서는 안 띄운다.
 * 🔴 시각 안전: 깜빡이지 않는다(정지 화면 · 움직임 없음).
 */
export function RestGuard() {
  const { pathname } = useLocation();
  const [due, setDue] = useState(false);
  const [asking, setAsking] = useState(false);
  const clockRef = useRef<RestClock | null>(loadRestClock());
  const adultPage = ADULT_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  useEffect(() => {
    const tick = () => {
      const prefs = loadRestPrefs(); // 보호자 화면에서 바꾸면 다음 세기부터 따른다
      if (prefs.minutes === 0 || classMode()) {
        setDue(false);
        return;
      }
      // 덮개가 떠 있는 동안·어른 화면에서는 안 센다
      const counting = !adultPage && document.visibilityState === "visible";
      const next = restTick(clockRef.current, Date.now(), counting);
      clockRef.current = next;
      saveRestClock(next);
      setDue(restDue(next, prefs.minutes));
    };
    tick();
    const t = window.setInterval(tick, TICK_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(t);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [adultPage]);

  if (!due || adultPage) return null;

  return (
    <div className="rest" role="dialog" aria-modal="true" aria-labelledby="rest-h">
      <div className="rest__card">
        <p className="rest__art" aria-hidden="true">
          🐤💤
        </p>
        <h2 id="rest-h" className="rest__title">
          병아리가 쉬자고 해요
        </h2>
        <p className="rest__say">
          {isNight(new Date()) ? "이제 잘 시간이 가까워요. 눈을 감고 쉬어요." : "눈을 쉬고, 기지개를 켜고, 물 한 모금 마셔요."}
        </p>
        {asking ? (
          <AdultGate
            what="이어서 볼지 정하는"
            onPass={() => {
              const fresh = { usedMs: 0, lastTick: Date.now() };
              clockRef.current = fresh;
              saveRestClock(fresh);
              setAsking(false);
              setDue(false);
            }}
          />
        ) : (
          <button type="button" className="btn btn--sm rest__adult" onClick={() => setAsking(true)}>
            어른이 이어서 보기
          </button>
        )}
      </div>
    </div>
  );
}

/** 🌙 밤에는 차분하게 — 누름을 통과시키는 반투명 덮개(화면 전체 filter 는 고정 위치 요소를 망가뜨려 쓰지 않는다) */
export function NightShade() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const check = () => setOn(loadRestPrefs().night && isNight(new Date()));
    check();
    const t = window.setInterval(check, 60_000);
    return () => window.clearInterval(t);
  }, []);
  return on ? <div className="night-shade" aria-hidden="true" /> : null;
}
